import {
  arrayBufferToBase64,
  base64ToArrayBuffer,
  wrapKey,
  unwrapKey,
  encryptWithKey,
  decryptWithKey
} from '~/utils/crypto'

/**
 * Composable for client-side AES-GCM encryption of user content.
 *
 * All sensitive data (note bodies, task titles, list items, etc.) is encrypted
 * before leaving the device. The DEK (Data Encryption Key) is wrapped with a
 * device-level SPK (Session Protector Key) before being stored in localStorage.
 *
 * The SPK is a non-extractable AES-KW key stored in IndexedDB. Because it is
 * non-extractable, an attacker who gains XSS access cannot exfiltrate the raw
 * key bytes and decrypt data offline — they would have to operate within the
 * browser context. The DEK wrapped blob in localStorage is useless without the
 * SPK from IDB.
 *
 * Encrypted payloads use the format `ivBase64.ciphertextBase64` — a 12-byte
 * random IV concatenated with the AES-GCM ciphertext via a dot separator.
 *
 * The DEK never leaves the client and is not transmitted to the server.
 */
export function useEncryption() {
  const STORAGE_KEY = 'minimind_encryption_key'
  const LEGACY_KEY_STORAGE = 'minimind_legacy_encryption_key'

  const IDB_DB_NAME = 'minimind_key_store'
  const IDB_STORE_NAME = 'session_keys'
  const IDB_SPK_KEY = 'spk'

  // === SPK management (IndexedDB) ===

  function openKeyStore(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(IDB_DB_NAME, 1)
      req.onupgradeneeded = (e) => {
        const db = (e.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
          db.createObjectStore(IDB_STORE_NAME)
        }
      }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  }

  async function getSPK(): Promise<CryptoKey | null> {
    const db = await openKeyStore()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE_NAME, 'readonly')
      const req = tx.objectStore(IDB_STORE_NAME).get(IDB_SPK_KEY)
      req.onsuccess = () => resolve((req.result as CryptoKey) ?? null)
      req.onerror = () => reject(req.error)
    })
  }

  async function getOrCreateSPK(): Promise<CryptoKey> {
    const existing = await getSPK()
    if (existing) return existing

    const spk = await crypto.subtle.generateKey(
      { name: 'AES-KW', length: 256 },
      false, // non-extractable: stored in IDB via structured clone, never exportable as raw bytes
      ['wrapKey', 'unwrapKey']
    )

    const db = await openKeyStore()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE_NAME, 'readwrite')
      const req = tx.objectStore(IDB_STORE_NAME).put(spk, IDB_SPK_KEY)
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })

    return spk
  }

  // === DEK storage (localStorage, wrapped with SPK) ===

  /**
   * Wraps the DEK with the device SPK and persists it in localStorage.
   * AES-KW produces a 40-byte blob (32-byte key + 8-byte integrity check).
   */
  async function storeKeyInSession(key: CryptoKey): Promise<void> {
    const spk = await getOrCreateSPK()
    const wrapped = await wrapKey(key, spk)
    localStorage.setItem(STORAGE_KEY, wrapped)
  }

  /**
   * Retrieves and unwraps the DEK from localStorage using the device SPK.
   *
   * Handles transparent migration: if the stored value is a raw 32-byte key
   * (old format from before SPK wrapping), it imports it and immediately
   * re-stores it in the new wrapped format.
   *
   * If the SPK was cleared from IDB while the wrapped blob still exists in
   * localStorage (e.g. partial site data clear), the stale entry is discarded
   * and null is returned — the user must re-authenticate.
   */
  async function getKeyFromSession(): Promise<CryptoKey | null> {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null

    const buffer = base64ToArrayBuffer(stored)

    if (buffer.byteLength === 40) {
      // New format: SPK-wrapped DEK
      const spk = await getSPK()
      if (!spk) {
        localStorage.removeItem(STORAGE_KEY)
        return null
      }
      return unwrapKey(stored, spk)
    }

    // Legacy format: raw DEK (32 bytes) — migrate silently
    const key = await crypto.subtle.importKey(
      'raw',
      buffer,
      'AES-GCM',
      true,
      ['encrypt', 'decrypt']
    )
    await storeKeyInSession(key)
    return key
  }

  /**
   * Checks synchronously whether an encryption key exists in storage.
   */
  function hasEncryptionKey(): boolean {
    return localStorage.getItem(STORAGE_KEY) !== null
  }

  /**
   * Removes the wrapped DEK from localStorage (used on sign out).
   * The SPK in IDB is device-level and intentionally kept across sign-outs;
   * it protects nothing without the matching wrapped DEK in localStorage.
   */
  function clearEncryptionKey(): void {
    localStorage.removeItem(STORAGE_KEY)
  }

  // === Legacy key storage (used during key migration) ===

  /**
   * Wraps the legacy seed-derived key with the device SPK and persists it,
   * so sync can use it as fallback for data encrypted before key wrapping.
   */
  async function storeLegacyKey(key: CryptoKey): Promise<void> {
    const spk = await getOrCreateSPK()
    const wrapped = await wrapKey(key, spk)
    localStorage.setItem(LEGACY_KEY_STORAGE, wrapped)
  }

  /**
   * Retrieves and unwraps the legacy encryption key from localStorage.
   * Applies the same format detection and migration logic as getKeyFromSession.
   */
  async function getLegacyKey(): Promise<CryptoKey | null> {
    const stored = localStorage.getItem(LEGACY_KEY_STORAGE)
    if (!stored) return null

    const buffer = base64ToArrayBuffer(stored)

    if (buffer.byteLength === 40) {
      const spk = await getSPK()
      if (!spk) {
        localStorage.removeItem(LEGACY_KEY_STORAGE)
        return null
      }
      return unwrapKey(stored, spk)
    }

    // Legacy raw format — migrate
    const key = await crypto.subtle.importKey(
      'raw',
      buffer,
      'AES-GCM',
      true,
      ['encrypt', 'decrypt']
    )
    await storeLegacyKey(key)
    return key
  }

  /**
   * Removes the legacy encryption key from storage.
   */
  function clearLegacyKey(): void {
    localStorage.removeItem(LEGACY_KEY_STORAGE)
  }

  /**
   * Encrypts a JSON-serializable object using the session encryption key.
   * @returns A string in the format "ivBase64.ciphertextBase64".
   */
  async function encryptPayload(data: Record<string, unknown>): Promise<string> {
    const key = await getKeyFromSession()
    if (!key) throw new Error('Encryption key not available')
    return encryptWithKey(data, key)
  }

  /**
   * Decrypts an encrypted payload string back into the original object.
   * @param encrypted String in "ivBase64.ciphertextBase64" format.
   */
  async function decryptPayload(encrypted: string): Promise<Record<string, unknown>> {
    const key = await getKeyFromSession()
    if (!key) throw new Error('Encryption key not available')
    return decryptWithKey(encrypted, key)
  }

  async function decryptPayloadWithFallback(
    encrypted: string
  ): Promise<{ decrypted: Record<string, unknown>; wasMigrated: boolean }> {
    try {
      const decrypted = await decryptPayload(encrypted)
      return { decrypted, wasMigrated: false }
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'OperationError')) throw e
      console.warn('[encryption] Fallback: trying legacy key')
      const oldKey = await getLegacyKey()
      if (!oldKey) throw new Error('No legacy key available for migration — re-authenticate with your seed phrase')
      const decrypted = await decryptWithKey(encrypted, oldKey)
      return { decrypted, wasMigrated: true }
    }
  }

  return {
    storeKeyInSession,
    getKeyFromSession,
    hasEncryptionKey,
    clearEncryptionKey,
    storeLegacyKey,
    getLegacyKey,
    clearLegacyKey,
    encryptPayload,
    decryptPayload,
    decryptPayloadWithFallback
  }
}
